import uniqueId from 'lodash/uniqueId';
import { Link } from 'found';
import PropTypes from 'prop-types';
import React, {
  useCallback,
  useMemo,
  Suspense,
  useRef,
  useState,
  useEffect,
} from 'react';
import { useMediaQuery } from 'react-responsive';
import { Carousel } from 'react-responsive-carousel';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { format } from 'date-fns';
import CardActions from 'components/Card/Actions';
import CardHTML from 'components/HTMLFragment';
import CardText from 'components/Card/Text';
import Image from 'components/Image';
import Video from 'components/Video';
import strip from 'strip';
import { useAuthContext } from 'AuthContext';
import tracker from 'lib/tracker';
import EventLike from 'ripple/Events/Components/EventLike';
import ShareButton from 'components/buttons/Share';
import Tooltip from 'ripple/Components/Tooltip';
import MuiCardActions from '@mui/material/CardActions';
import { getEventHashtags, getSelectionDate } from 'lib/eventUtils';
import useIsMobile from 'lib/useIsMobile';
import isSameDay from 'date-fns/is_same_day';
import WinChallengeLearnMore from 'assets/WantToWin.png';
import CanonWinTikTok from 'assets/CanonWinTikTok.png';
import { useTheme } from '@mui/material';
import styles from './index.scss';

// eslint-disable-next-line import/no-unresolved
const Application = React.lazy(() => import('application/Application'));

const learnMoreBanner = {
  heinz57inviteonly: WinChallengeLearnMore,
  heinz57: WinChallengeLearnMore,
  canonivy2holiday: CanonWinTikTok,
};

const HeroCarousel = ({
  playlist,
  compact,
  style,
  event,
  viewer,
  refreshActivities,
  ...props
}) => {
  const theme = useTheme();
  const mobile = useIsMobile();
  const applicationRef = useRef(null);
  const [applicationMF, setApplicationMF] = useState(null);
  const { showSignInDialog } = useAuthContext();
  const tablet = useMediaQuery({ maxWidth: 960 });
  const eventId = event?.id;
  const personId = viewer?.personInfo ? viewer?.personInfo?.id : null;

  const trackerData = useMemo(
    () => ({
      event,
      viewer,
    }),
    [event, viewer],
  );

  const handleItemClick = useCallback(
    (_index, item) => {
      if (!compact && item.type === 'a') {
        const el = document.getElementById(item.key);
        if (el) {
          el.click();
          tracker.trackGoogleAnalyticsEvent(
            'Events',
            `Hero Image Clicked - ${event?.id}`,
            trackerData,
          );
        }
      }
    },
    [compact, event, trackerData],
  );

  const renderImage = useCallback(
    (asset, key, url) => {
      const img = (
        <Image
          alt={asset && asset.description}
          src={asset && asset.hlsPlaylistUrl}
          style={style}
        />
      );
      return url ? (
        <a
          href={url}
          id={key}
          key={key}
          target='_blank'
          rel='noopener noreferrer'
        >
          {img}
        </a>
      ) : (
        <span key={key}>{img}</span>
      );
    },
    [style],
  );

  const renderVideo = useCallback(
    (asset, key, url) => {
      let sources = [];
      if (asset.provider && asset.basename) {
        sources = asset.basename;
      } else if (asset.hlsPlaylistUrl) {
        sources = asset.hlsPlaylistUrl;
      } else {
        if (asset.mp4Url) {
          sources.push({
            src: asset.mp4Url,
            type: 'video/mp4',
          });
        }
        if (asset.webmUrl) {
          sources.push({
            src: asset.webmUrl,
            type: 'video/webm',
          });
        }
      }
      return <Video key={key} href={url} sources={sources} style={style} />;
    },
    [style],
  );

  const renderItem = useCallback(
    (item, c = false) => {
      if (item) {
        return item?.type === 'Video'
          ? renderVideo(item, item?.assetId, !c && item?.hlsPlaylistUrl)
          : renderImage(item, item?.assetId, !c && item?.hlsPlaylistUrl);
      }
      return renderImage(null, uniqueId());
    },
    [renderImage, renderVideo],
  );

  const shareProps = {
    title: `${strip(event?.community?.name)} ${strip(event?.name)}`,
  };

  const partyRange = () => {
    if (
      !isSameDay(
        event?.milestones?.partyStartDate,
        event?.milestones?.partyEndDate,
      )
    ) {
      return `${format(event?.milestones?.partyStartDate, 'MMMM D')} - ${format(
        event?.milestones?.partyEndDate,
        'MMMM D',
      )}`;
    }
    return `${format(event?.milestones?.partyStartDate, 'MMMM D')}`;
  };

  useEffect(() => {
    if (applicationRef.current && eventId) {
      setApplicationMF(
        <Suspense fallback='Loading'>
          <Application
            context={{
              eventId,
              personId,
              sponsorId: event?.community?.id,
              loggedIn: viewer?.loggedIn,
              email: viewer?.personInfo?.email,
              showSignInDialog,
              categories: (event?.categories || []).map(c => c.name),
              eventType: event.eventType,
              eventOptinConfiguration: event.eventOptinConfiguration,
              selectionDate: getSelectionDate(event),
            }}
            callback={refreshActivities}
          />
        </Suspense>,
      );
    }
  }, [
    event,
    eventId,
    personId,
    refreshActivities,
    showSignInDialog,
    viewer?.loggedIn,
    viewer?.personInfo?.email,
  ]);
  return (
    <>
      <div
        style={
          mobile || tablet
            ? {}
            : {
                display: 'flex',
                flexDirection: 'row-reverse',
                marginBottom: 24,
              }
        }
      >
        {compact
        || !playlist
        || !playlist.assets
        || playlist.assets.length < 2 ? (
          <div
            className={
              mobile || tablet ? styles.containerFull : styles.container
            }
          >
            {renderItem(playlist?.assets[0], compact)}
          </div>
        ) : (
          <Carousel
            autoPlay={playlist.autoPlay}
            className={
              mobile || tablet ? styles.containerFull : styles.container
            }
            infiniteLoop
            interval={7000}
            onClickItem={handleItemClick}
            showStatus={false}
            showThumbs={false}
            stopOnHover
            {...props}
          >
            {playlist.assets.map(item => renderItem(item))}
          </Carousel>
        )}
        {!compact && (
          <Card
            style={
              mobile || tablet
                ? {
                    background: `linear-gradient(to right, ${theme.palette.background.default} 71%, ${theme.palette.background.default})`,
                    boxShadow: 'none',
                  }
                : {
                    zIndex: 1,
                    background: `linear-gradient(to right, ${theme.palette.background.default} 99%, ${theme.palette.background.default}`,
                    boxShadow: 'none',
                    width: '41.5%',
                    // height: '30.3vw',
                    paddingLeft: '2%',
                    display: 'flex',
                    alignItems: 'flex-end',
                  }
            }
          >
            <CardContent
              style={
                mobile || tablet
                  ? {}
                  : {
                      bottom: 0,
                      width: '90%',
                      padding: '5px 5% 0 5px',
                    }
              }
            >
              {learnMoreBanner[event?.slug] && (
                <Link to={`/events/${event.slug}/tiktok-challenge`}>
                  <img
                    style={
                      mobile || tablet
                        ? {}
                        : { width: '20vw', textAlign: 'center' }
                    }
                    src={learnMoreBanner[event.slug]}
                    alt='Want to Win Challenge'
                  />
                </Link>
              )}
              <CardText style={{ paddingLeft: 5 }}>
                <CardHTML
                  style={{
                    fontSize: mobile || tablet ? '1.5em' : '1.5vw',
                    fontWeight: 500,
                  }}
                >
                  {event?.community?.name}
                </CardHTML>
                <div
                  style={{
                    fontSize: mobile || tablet ? '2em' : '2.5vw',
                    fontWeight: 700,
                  }}
                >
                  {event?.name}
                </div>
              </CardText>
              {event?.eventType === 'HOUSEPARTY' && (
                <CardText
                  style={{
                    paddingTop: '0px',
                    paddingLeft: 5,
                  }}
                >
                  <CardHTML
                    style={{
                      fontSize: mobile || tablet ? '1.3em' : '1.3vw',
                      fontWeight: 600,
                    }}
                  >
                    {`Party Date: ${partyRange()}`}
                  </CardHTML>
                </CardText>
              )}
              <CardActions>
                <div ref={applicationRef} style={{ width: '100%' }}>
                  {applicationMF}
                </div>
              </CardActions>
              <MuiCardActions>
                <EventLike
                  event={event}
                  viewer={viewer}
                  liked={event?.liked} // this key not there
                  likeCount={event?.likeCount}
                />
                <Tooltip title='Share' arrow placement='top' size='medium'>
                  <div>
                    <ShareButton
                      style={{
                        backgroundColor: 'transparent',
                        border: `3px solid ${theme.palette.text.primary}`,
                        minWidth: 45,
                        height: 45,
                        borderRadius: '50%',
                      }}
                      hashtags={getEventHashtags(event?.eventData)}
                      small={false}
                      iconStyle={{
                        width: 30,
                        height: 30,
                        marginTop: -3,
                        marginLeft: 0,
                        marginRight: 0,
                        color: theme.palette.text.primary,
                        transform: 'scaleX(-1)',
                      }}
                      primary
                      {...shareProps}
                    />
                  </div>
                </Tooltip>
              </MuiCardActions>
            </CardContent>
          </Card>
        )}
      </div>
    </>
  );
};

HeroCarousel.propTypes = {
  playlist: PropTypes.object,
  compact: PropTypes.bool,
  style: PropTypes.object,
  event: PropTypes.object,
  viewer: PropTypes.object,
  refreshActivities: PropTypes.func,
};

export default HeroCarousel;
